import React, { useState } from 'react';
import { MessageSquare, CheckCircle, Star, TrendingUp, Download } from 'lucide-react';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import './ResponseAnalytics.css';

const ResponseAnalytics: React.FC = () => {
  const {
    analyticsStats,
    barChartData,
    ratingDistribution,
    recentResponses,
    surveyOptions,
  } = useFeedbackManagementStore();

  const [selectedSurvey, setSelectedSurvey] = useState('course-feedback');

  // Helper function to get icon component based on icon name
  const getIcon = (iconName: string): React.ReactElement => {
    const icons: { [key: string]: React.ReactElement } = {
      'MessageSquare': <MessageSquare size={20} />,
      'CheckCircle': <CheckCircle size={20} />,
      'Star': <Star size={20} />,
      'TrendingUp': <TrendingUp size={20} />,
    };
    return icons[iconName] || <MessageSquare size={20} />;
  };

  return (
    <div className="s-admin-feedback-response-analytics">
      <div className="s-admin-feedback-analytics-header">
        <div className="s-admin-feedback-analytics-title-section">
          <h3>Response Analytics</h3>
          <p>Analyze survey responses and feedback</p>
        </div>
        <div className="s-admin-feedback-analytics-controls">
          <select
            className="s-admin-feedback-survey-selector"
            value={selectedSurvey}
            onChange={(e) => setSelectedSurvey(e.target.value)}
          >
            {surveyOptions?.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="s-admin-feedback-export-btn">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="s-admin-feedback-stats-grid">
        {analyticsStats?.map((stat, index) => (
          <div key={index} className="s-admin-feedback-stat-card">
            <div className="s-admin-feedback-stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
              {getIcon(stat.icon)}
            </div>
            <div className="s-admin-feedback-stat-info">
              <p className="s-admin-feedback-stat-label">{stat.label}</p>
              <h3 className="s-admin-feedback-stat-value">{stat.value}</h3>
              <p className="s-admin-feedback-stat-subtext">{stat.subtext}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="s-admin-feedback-analytics-charts">
        <div className="s-admin-feedback-chart-section s-admin-feedback-bar-chart-section">
          <h4>Average Rating By Question</h4>
          <p className="s-admin-feedback-chart-subtitle">Monthly feedback submissions</p>

          <div className="s-admin-feedback-bar-chart">
            <div className="s-admin-feedback-chart-y-axis">
              <span>5</span>
              <span>4</span>
              <span>3</span>
              <span>2</span>
              <span>1</span>
            </div>
            <div className="s-admin-feedback-chart-bars">
              {barChartData?.map((item, index) => (
                <div key={index} className="s-admin-feedback-bar-item">
                  <div className="s-admin-feedback-bar-container">
                    <div className="s-admin-feedback-bar" style={{ height: `${item.height}%` }}>
                      <span className="s-admin-feedback-bar-value">{item.value}</span>
                    </div>
                  </div>
                  <div className="s-admin-feedback-bar-label">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="s-admin-feedback-chart-section s-admin-feedback-pie-chart-section">
          <h4>Rating Distribution</h4>
          <p className="s-admin-feedback-chart-subtitle">Survey feedback ratings breakdown</p>

          <div className="s-admin-feedback-pie-chart-container">
            <svg viewBox="0 0 200 200" className="s-admin-feedback-pie-chart">
              {ratingDistribution && (() => {
                let cumulativePercentage = 0;
                const centerX = 100;
                const centerY = 100;
                const radius = 80;

                const getCoordinatesForPercent = (percent: number) => {
                  const x = centerX + radius * Math.cos(2 * Math.PI * percent);
                  const y = centerY + radius * Math.sin(2 * Math.PI * percent);
                  return { x, y };
                };

                return ratingDistribution.map((slice, index) => {
                  const startPercentage = cumulativePercentage;
                  cumulativePercentage += slice.percentage / 100;
                  const endPercentage = cumulativePercentage;

                  const start = getCoordinatesForPercent(startPercentage - 0.25);
                  const end = getCoordinatesForPercent(endPercentage - 0.25);

                  const largeArcFlag = slice.percentage > 50 ? 1 : 0;

                  const pathData = [
                    `M ${centerX} ${centerY}`,
                    `L ${start.x} ${start.y}`,
                    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
                    'Z'
                  ].join(' ');

                  return (
                    <path
                      key={index}
                      d={pathData}
                      fill={slice.color}
                    />
                  );
                });
              })()}
              <circle cx="100" cy="100" r="45" fill="var(--color-greyish-bg)" />
              <text x="100" y="95" textAnchor="middle" fontSize="28" fontWeight="bold" fill="var(--color-black-text)">54%</text>
              <text x="100" y="115" textAnchor="middle" fontSize="12" fill="var(--color-secondary-text)">5 Stars</text>
            </svg>

            <div className="s-admin-feedback-rating-legend">
              {ratingDistribution?.map((item) => (
                <div key={item.stars} className="s-admin-feedback-legend-item">
                  <div className="s-admin-feedback-legend-left">
                    <div className="s-admin-feedback-legend-color" style={{ backgroundColor: item.color }} />
                    <span className="s-admin-feedback-legend-label">{item.stars} Stars:</span>
                  </div>
                  <span className="s-admin-feedback-legend-value">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="s-admin-feedback-recent-responses">
        <div className="s-admin-feedback-responses-header">
          <h4>Recent Text Responses</h4>
          <button className="s-admin-feedback-view-all-btn">View All →</button>
        </div>

        <div className="s-admin-feedback-responses-list">
          {recentResponses?.map((response) => (
            <div key={response.id} className="s-admin-feedback-response-item">
              <div className="s-admin-feedback-response-user">
                <div className="s-admin-feedback-user-avatar">{response.name.charAt(0)}</div>
                <div className="s-admin-feedback-user-info">
                  <div className="s-admin-feedback-user-name">{response.name}</div>
                  <div className="s-admin-feedback-response-time">{response.time}</div>
                </div>
              </div>
              <div className="s-admin-feedback-response-content">
                <div className="s-admin-feedback-response-rating">
                  {'⭐'.repeat(response.rating)}
                </div>
                <p className="s-admin-feedback-response-comment">{response.comment}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResponseAnalytics;
